import { useTranslation } from 'react-i18next';
import { Activity, CheckCircle2, AlertTriangle, CircleDot, Loader2 } from 'lucide-react';
import { Badge, Button, cn } from '@scio3/ui';
import { sessionLabel, SessionStatus } from '../hooks/useDeviceSessionStream';
import type { DeviceTroubleshootSessionSummary } from '../hooks/useDeviceTroubleshootHistory';

interface Props {
  sessions: DeviceTroubleshootSessionSummary[];
  selectedId?: string | null;
  onSelect: (sessionId: string) => void;
  loading?: boolean;
}

const ACTIVE = new Set<string>([SessionStatus.Creating, SessionStatus.Running]);

/** The durable, chat-independent entry point: a list of troubleshooting sessions (status · device · when
 *  · who · how long). Clicking a row opens its read-only history; an active row resumes live. */
export function DeviceTroubleshootSessionList({ sessions, selectedId, onSelect, loading }: Props) {
  const { t } = useTranslation();

  // First load (no rows yet) → a centered spinner, matching the other troubleshoot surfaces. A background
  // refetch (loading with rows already present) keeps the list visible — no list→spinner→list flash.
  if (loading && sessions.length === 0) {
    return (
      <div
        className="flex items-center justify-center p-6"
        role="status"
        aria-label={t('optibot.session.history.loading')}
        data-testid="device-troubleshoot-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!loading && sessions.length === 0) {
    return (
      <div
        className="p-6 text-center text-sm text-muted-foreground"
        data-testid="device-troubleshoot-empty"
      >
        {t('optibot.session.history.empty')}
      </div>
    );
  }

  return (
    <ul className="divide-y divide-border" data-testid="device-troubleshoot-list">
      {sessions.map((s) => (
        <li key={s.sessionId}>
          <Button
            variant="ghost"
            onClick={() => onSelect(s.sessionId)}
            className={cn(
              'flex h-auto w-full items-start justify-start gap-3 rounded-none px-3 py-2.5 text-left',
              selectedId === s.sessionId && 'bg-muted',
            )}
          >
            <StatusIcon status={s.status} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-foreground">
                  {sessionLabel(s) ?? s.deviceUUID}
                </span>
                <StatusBadge status={s.status} />
                {s.mode === 'staff' && (
                  <Badge variant="outline" className="text-[10px]">
                    {t('optibot.session.history.staff')}
                  </Badge>
                )}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {formatStarted(s.startedAt)} · {t('optibot.session.history.by', { actor: s.actor })}{' '}
                · {formatDuration(s.startedAt, s.endedAt)}
              </div>
            </div>
          </Button>
        </li>
      ))}
    </ul>
  );
}

function StatusIcon({ status }: { status: string }) {
  const Icon =
    status === SessionStatus.Failed
      ? AlertTriangle
      : ACTIVE.has(status)
        ? Activity
        : status === SessionStatus.Ended
          ? CheckCircle2
          : CircleDot;
  return (
    <Icon
      className={cn(
        'mt-0.5 h-4 w-4 shrink-0',
        status === SessionStatus.Failed
          ? 'text-destructive'
          : ACTIVE.has(status)
            ? 'text-primary'
            : 'text-muted-foreground',
      )}
    />
  );
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const variant =
    status === SessionStatus.Failed ? 'destructive' : ACTIVE.has(status) ? 'default' : 'secondary';
  return (
    <Badge variant={variant} className="text-[10px]">
      {t(`optibot.session.status.${status}`, status)}
    </Badge>
  );
}

function formatStarted(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** "running" while open; else a compact h/m/s duration. */
function formatDuration(startIso: string, endIso?: string | null): string {
  const start = Date.parse(startIso);
  const end = endIso ? Date.parse(endIso) : NaN;
  if (Number.isNaN(start) || Number.isNaN(end)) return '—';
  const s = Math.max(0, Math.round((end - start) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}
